import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Gift, AlertTriangle, FileText, Sparkles, AlertCircle, CalendarCheck, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const DISMISS_KEY = 'today_events_banner_dismissed_date';

const TYPE_META = {
  birthday:         { label: 'Aniversário', Icon: Gift,           color: 'text-pink-300',    link: '/community' },
  project_deadline: { label: 'Entrega hoje', Icon: AlertTriangle, color: 'text-red-300',     link: '/projects' },
  demo_deadline:    { label: 'Demo hoje',    Icon: FileText,      color: 'text-orange-300',  link: '/projects' },
  project_created:  { label: 'Criado hoje',  Icon: Sparkles,      color: 'text-green-300',   link: '/projects' },
  invoice_pending:  { label: 'Retake',       Icon: AlertCircle,   color: 'text-yellow-300',  link: '/retakes' },
};

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Banner fino no topo do Layout mostrando eventos de HOJE.
 * Dispensável pelo usuário (persistido em localStorage por dia).
 */
export const TodayEventsBanner = ({ getAuthHeaders }) => {
  const [events, setEvents] = useState([]);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === todayIso()
  );

  useEffect(() => {
    if (dismissed) return;
    let cancelled = false;
    axios.get(`${API}/calendar/events`, { headers: getAuthHeaders() })
      .then(res => {
        if (cancelled) return;
        const today = todayIso();
        setEvents((res.data || []).filter(e => e?.date === today));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [getAuthHeaders, dismissed]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, todayIso());
    setDismissed(true);
  };

  if (dismissed || events.length === 0) return null;

  return (
    <div
      className="bg-gradient-to-r from-teal-600/30 via-purple-600/30 to-pink-600/30 border-b border-teal-500/30"
      data-testid="today-events-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-3 text-sm">
        <CalendarCheck className="h-4 w-4 text-teal-300 shrink-0" />
        <span className="text-white font-medium shrink-0">Hoje:</span>
        <div className="flex-1 flex items-center gap-3 flex-wrap min-w-0">
          {events.slice(0, 4).map((e, idx) => {
            const meta = TYPE_META[e.type] || { label: 'Evento', Icon: Sparkles, color: 'text-blue-300', link: '/community' };
            const { Icon, color, label, link } = meta;
            return (
              <Link
                key={`today-${e.type}-${e.title}-${idx}`}
                to={link}
                className="flex items-center gap-1.5 text-xs text-slate-200 hover:text-white hover:underline"
                data-testid={`today-event-${idx}`}
              >
                <Icon className={`h-3 w-3 ${color}`} />
                <span className={`${color} font-medium`}>{label}:</span>
                <span className="truncate max-w-[14rem]">{e.title}</span>
              </Link>
            );
          })}
          {events.length > 4 && (
            <Link to="/community" className="text-xs text-slate-300 hover:text-white underline">
              +{events.length - 4} mais
            </Link>
          )}
        </div>
        <button
          onClick={dismiss}
          className="shrink-0 text-slate-400 hover:text-white p-1 rounded transition-colors"
          aria-label="Dispensar banner de eventos"
          data-testid="today-events-dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};
